import { Switch } from '@nextui-org/react'
import { Button } from '~/components/common/Button'
import { IoSearchOutline } from '@react-icons/all-files/io5/IoSearchOutline'
import { HiOutlineFilter } from '@react-icons/all-files/hi/HiOutlineFilter'
import { RiCloseLine } from '@react-icons/all-files/ri/RiCloseLine'
import { useState, useMemo } from 'react'
import {
  EndpointSelectionProps,
  createEndpointKey,
  toSelectedEndpoint,
  MigrationEndpoint,
  EndpointCategory,
  ENDPOINT_CATEGORY_CONFIGS
} from '~/types/migration'
import { EndpointItem } from './EndpointItem'
import { ModalId, useModalController } from '~/hooks/useModal'
import { TaskStatusDetailModalProps } from '../modals/TaskStatusDetailsModal'

interface MigrationEndpointSelectorProps extends EndpointSelectionProps {
  /** When true, renders without modal styling (no header, flexible width, no shadow) */
  inline?: boolean
  /** Optional callback for cancel/close action */
  onCancel?: () => void
}

export function MigrationEndpointSelector({
  endpoints,
  selectedEndpoints,
  onSelectionChange,
  onSubmit,
  isSubmitting,
  showSubmitButton = true,
  inline = false,
  onCancel
}: MigrationEndpointSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState<Set<EndpointCategory>>(
    new Set(ENDPOINT_CATEGORY_CONFIGS.map(config => config.category))
  )

  const { closeModal } = useModalController<TaskStatusDetailModalProps>(ModalId.TaskStatusDetails)

  const handleClose = () => {
    if (onCancel) {
      onCancel()
    } else {
      closeModal()
    }
  }

  // Filter and group endpoints by category
  const filteredAndGroupedEndpoints = useMemo(() => {
    const filtered = endpoints.filter(endpoint => {
      const matchesSearch = endpoint.name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesCategory = selectedCategories.has(endpoint.category as EndpointCategory)
      return matchesSearch && matchesCategory
    })

    // Group by category
    const grouped = filtered.reduce(
      (acc, endpoint) => {
        const category = endpoint.category as EndpointCategory
        if (!acc[category]) {
          acc[category] = []
        }
        acc[category].push(endpoint)
        return acc
      },
      {} as Record<EndpointCategory, MigrationEndpoint[]>
    )

    return grouped
  }, [endpoints, searchQuery, selectedCategories])

  // Get all filtered endpoints for selection logic
  const allFilteredEndpoints = useMemo(() => {
    return Object.values(filteredAndGroupedEndpoints).flat()
  }, [filteredAndGroupedEndpoints])

  const handleEndpointToggle = (endpoint: MigrationEndpoint, isSelected: boolean) => {
    let newSelected: MigrationEndpoint[]

    if (isSelected) {
      newSelected = [...selectedEndpoints, endpoint]
    } else {
      newSelected = selectedEndpoints.filter(
        selected => createEndpointKey(selected) !== createEndpointKey(endpoint)
      )
    }

    onSelectionChange(newSelected)
  }

  const handleCategoryToggle = (category: EndpointCategory) => {
    const newCategories = new Set(selectedCategories)
    if (newCategories.has(category)) {
      newCategories.delete(category)
    } else {
      newCategories.add(category)
    }
    setSelectedCategories(newCategories)
  }

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      onSelectionChange(allFilteredEndpoints)
    } else {
      onSelectionChange([])
    }
  }

  const handleSubmit = () => {
    const selectedEndpointsForSubmission = selectedEndpoints.map(toSelectedEndpoint)
    onSubmit(selectedEndpointsForSubmission)
  }

  const isAllSelected =
    allFilteredEndpoints.length > 0 &&
    allFilteredEndpoints.every((endpoint: MigrationEndpoint) =>
      selectedEndpoints.some(
        selected => createEndpointKey(selected) === createEndpointKey(endpoint)
      )
    )

  const containerClassName = inline
    ? 'bg-[rgba(0,0,0,0.02)] rounded-[18px] w-full flex flex-col'
    : 'bg-white rounded-[28px] shadow-[0px_6px_16px_0px_rgba(0,0,0,0.08),0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)] w-[680px] h-[740px] flex flex-col'

  return (
    <div className={containerClassName}>
      {/* Modal Header - hidden in inline mode */}
      {!inline && (
        <div
          className='flex items-center justify-between border-b border-[#eaeaea] h-[60px]'
          style={{ display: 'flex', padding: '16px', alignItems: 'center', gap: '8px' }}
        >
          <h2
            className='text-[14px] font-bold text-black leading-[18px]'
            style={{ fontFamily: 'Geist, sans-serif', fontWeight: 700 }}
          >
            Select Endpoints
          </h2>
          <button
            onClick={handleClose}
            className='transition-colors'
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(0, 0, 0, 0.45)'
            }}
          >
            <RiCloseLine className='w-6 h-6' />
          </button>
        </div>
      )}

      {/* Modal Content */}
      <div
        className={
          inline ? 'px-4 py-3 flex flex-col' : 'px-6 py-4 flex flex-col flex-1 overflow-hidden'
        }
      >
        {/* Search Bar with Selected Endpoints and Filter Button */}
        <div className='flex items-center gap-4 mb-4'>
          <div className='relative' style={{ width: 'calc(100% - 60px)' }}>
            <div
              className='min-h-[40px] border border-[#d6d6d6] hover:border-gray-300 bg-white flex items-center gap-2 p-2 w-full'
              style={{ borderRadius: '12px' }}
            >
              <IoSearchOutline className='w-4 h-4 text-gray-400 flex-shrink-0' />

              {/* Selected endpoint chips - horizontal scrolling container with calculated width */}
              {selectedEndpoints.length > 0 && (
                <div className='flex items-center gap-2 overflow-x-auto scrollbar-hide flex-1 min-w-0'>
                  {selectedEndpoints.map(endpoint => (
                    <div
                      key={createEndpointKey(endpoint)}
                      className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm flex-shrink-0 max-w-[200px]'
                      style={{
                        backgroundColor: 'rgba(159, 159, 248, 0.3)',
                        color: 'rgba(0, 0, 0, 0.88)'
                      }}
                    >
                      <span
                        className='truncate'
                        style={{
                          fontFamily: 'Geist, sans-serif',
                          fontSize: '12px',
                          fontWeight: 500
                        }}
                        title={endpoint.name}
                      >
                        {endpoint.name}
                      </span>
                      <button
                        onClick={e => {
                          e.stopPropagation()
                          handleEndpointToggle(endpoint, false)
                        }}
                        className='hover:bg-black/10 rounded-full p-0.5 transition-colors flex-shrink-0'
                      >
                        <RiCloseLine className='w-3 h-3' />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search input */}
              {selectedEndpoints.length === 0 && (
                <input
                  type='text'
                  placeholder='Search or select...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='flex-1 outline-none bg-transparent text-sm'
                  style={{ fontFamily: 'Geist, sans-serif', fontSize: '14px' }}
                />
              )}
            </div>
          </div>
          <div className='relative flex-shrink-0'>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className='p-2 transition-colors'
              style={{
                backgroundColor: showFilters ? 'rgba(0, 0, 0, 0.06)' : 'transparent',
                borderRadius: '8px'
              }}
            >
              <HiOutlineFilter
                className='w-5 h-5'
                style={{
                  color: showFilters ? 'rgba(0, 0, 0, 0.88)' : 'rgba(0, 0, 0, 0.45)'
                }}
              />
            </button>

            {/* Filter Dropdown */}
            {showFilters && (
              <div className='absolute right-0 top-full mt-1 bg-white rounded-xl shadow-[0px_6px_16px_0px_rgba(0,0,0,0.08),0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)] w-[223px] p-2 z-10'>
                {/* Category Checkboxes */}
                {ENDPOINT_CATEGORY_CONFIGS.filter(config => {
                  // Only show categories that have endpoints
                  return endpoints.some(endpoint => endpoint.category === config.category)
                }).map(config => (
                  <div key={config.category} className='flex items-center gap-2 p-1 h-8 rounded-lg'>
                    <div className='w-4 h-4 flex items-center justify-center'>
                      {selectedCategories.has(config.category) && (
                        <svg className='w-4 h-4' viewBox='0 0 16 16' fill='#65558F'>
                          <path d='M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z' />
                        </svg>
                      )}
                    </div>
                    <button
                      onClick={() => handleCategoryToggle(config.category)}
                      className='text-[14px] font-medium cursor-pointer flex-1 text-left'
                      style={{
                        fontFamily: 'Geist, sans-serif',
                        lineHeight: '22px',
                        fontWeight: 500,
                        color: selectedCategories.has(config.category)
                          ? '#65558F'
                          : 'rgba(0, 0, 0, 0.88)'
                      }}
                    >
                      {config.label}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Selection Summary with Select All Toggle */}
        <div className='flex items-center mb-4' style={{ gap: '16px' }}>
          <div
            className='flex items-center'
            style={{
              display: 'flex',
              padding: '8px 2px',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Switch
              isSelected={isAllSelected}
              onValueChange={handleSelectAll}
              size='sm'
              classNames={{
                wrapper: 'h-5 w-9',
                thumb: 'w-3 h-3',
                thumbIcon: 'w-2 h-2'
              }}
              style={
                {
                  '--nextui-primary': '#65558F',
                  '--nextui-primary-50': 'rgba(101, 85, 143, 0.1)',
                  '--nextui-primary-100': 'rgba(101, 85, 143, 0.2)',
                  '--nextui-primary-200': 'rgba(101, 85, 143, 0.3)',
                  '--nextui-primary-300': 'rgba(101, 85, 143, 0.4)',
                  '--nextui-primary-400': 'rgba(101, 85, 143, 0.5)',
                  '--nextui-primary-500': '#65558F',
                  '--nextui-primary-600': '#65558F',
                  '--nextui-primary-700': '#65558F',
                  '--nextui-primary-800': '#65558F',
                  '--nextui-primary-900': '#65558F'
                } as React.CSSProperties
              }
            />
            <span
              style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                overflow: 'hidden',
                color: '#65558F',
                textOverflow: 'ellipsis',
                fontFamily: 'Geist',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal'
              }}
            >
              Select All
            </span>
          </div>
          <div
            className='text-[14px] font-semibold text-[rgba(0,0,0,0.65)]'
            style={{
              fontFamily: 'Geist Mono, monospace',
              lineHeight: 'normal',
              fontWeight: 600,
              fontSize: '14px'
            }}
          >
            {selectedEndpoints.length} Endpoints Selected
          </div>
        </div>

        {/* Horizontal divider */}
        <div className='border-t border-gray-200 mb-4'></div>

        {/* Endpoints List - Clean flat layout */}
        <div className={inline ? 'max-h-[400px] overflow-y-auto' : 'flex-1 overflow-y-auto'}>
          {allFilteredEndpoints.length === 0 ? (
            <div className='p-8 text-center text-gray-500'>
              No endpoints found matching your criteria
            </div>
          ) : (
            <div className='space-y-6'>
              {ENDPOINT_CATEGORY_CONFIGS.map((config, categoryIndex) => {
                const categoryEndpoints = filteredAndGroupedEndpoints[config.category] || []
                if (categoryEndpoints.length === 0) return null

                return (
                  <div key={config.category}>
                    {/* Category Header */}
                    <div className='mb-3'>
                      <span
                        className='uppercase tracking-wide font-semibold'
                        style={{
                          fontFamily: 'Geist Mono',
                          fontSize: '12px',
                          fontWeight: 600,
                          lineHeight: 'normal',
                          color: 'rgba(0, 0, 0, 0.65)'
                        }}
                      >
                        {config.label}
                      </span>
                    </div>

                    {/* Category Endpoints */}
                    <div className='space-y-3 mb-4'>
                      {categoryEndpoints.map((endpoint: MigrationEndpoint) => (
                        <EndpointItem
                          key={createEndpointKey(endpoint)}
                          endpoint={endpoint}
                          isSelected={selectedEndpoints.some(
                            selected => createEndpointKey(selected) === createEndpointKey(endpoint)
                          )}
                          onToggle={handleEndpointToggle}
                        />
                      ))}
                    </div>

                    {/* Horizontal divider between categories (except last) */}
                    {categoryIndex < ENDPOINT_CATEGORY_CONFIGS.length - 1 &&
                      categoryEndpoints.length > 0 && (
                        <div className='border-t border-gray-200 mb-4'></div>
                      )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {showSubmitButton && (
        <div
          className={
            inline
              ? 'px-4 py-3 flex justify-end gap-2'
              : 'bg-[#fcfcfc] border-t border-[#eaeaea] px-8 py-4 flex justify-end gap-2 rounded-b-[28px]'
          }
        >
          {!inline && (
            <Button
              variant='outlined'
              onClick={handleClose}
              disabled={isSubmitting}
              style={{
                fontFamily: 'Geist, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px'
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={selectedEndpoints.length === 0 || isSubmitting}
            style={{
              fontFamily: 'Geist, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              ...(selectedEndpoints.length === 0 || isSubmitting
                ? {
                    backgroundColor: 'rgba(29,27,32,0.12)',
                    color: 'rgba(38,38,38,0.20)',
                    cursor: 'not-allowed'
                  }
                : {})
            }}
          >
            {isSubmitting ? 'Generating...' : 'Select Endpoint(s)'}
          </Button>
        </div>
      )}
    </div>
  )
}
